// NEW: src/components/interactive-challenge-slide.jsx
//
// Interactive challenge slide. Used when a concept's enriched data ships an
// `interactiveChallenge` field. Supports four schema variants:
//
//   Variant A (Ch5 — single number, e.g. "Add 1+2+...+100"):
//     { inputType: "number", correctAnswer, revealSequence, ... }
//     UX: 1 input, auto-advancing reveal, 3 outcomes.
//
//   Variant B (Ch4 — find two numbers):
//     { inputType: "twoNumbers", answer: [a, b], orderSensitive, reveal, ... }
//     UX: 2 inputs, tap-to-advance reveal.
//
//   Variant C (Ch7 — match pairs):
//     { inputType: "matchPairs", leftColumn, rightColumn, correctMatches, ... }
//     UX: tap-left then tap-right to pair, tap-to-advance reveal.
//
//   Variant D (Ch1 — build a prime factorisation by tapping prime tiles):
//     {
//       type: "buildPrimeFactorisation",
//       prompt / promptHi,
//       target: 60,
//       primeTiles: [2, 3, 5, 7, 11, 13],
//       timerSeconds: N,
//       reveal: [{ en, hi }, ...],
//       timeoutMessage: { en, hi },
//     }
//     UX: tile grid → each tap multiplies the running product. Undo + Reset.
//     Auto-correct when running product equals target (FTA guarantees uniqueness).
//
// Pure render — own Prev/Next footer (matches concept-table-slide and
// concept-image-slide patterns). Parent hides the shared nav for this type.

import { useState, useEffect, useRef } from "react";
import { getFontFamily } from "../constants/colors";

const FADE_MS = 280;
const STEP_DELAY_MS = 1500;
const SUBMIT_HOLD_MS = 1500;

function pickLang(en, hi, lang) {
  if (lang === "hi" && typeof hi === "string" && hi.length > 0) return hi;
  return en || "";
}

function formatTimer(s) {
  const safe = Math.max(0, s);
  const m = Math.floor(safe / 60);
  const sec = safe % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function OutcomeCard({ outcome, message, ff }) {
  if (!message) return null;
  const palette = outcome === "correct"
    ? { border: "#1A7A52", bg: "#F0FAF4", emoji: "🎉" }
    : outcome === "wrong"
    ? { border: "#D89F4A", bg: "#FFF8E8", emoji: "💡" }
    : { border: "#9AA0A6", bg: "#F4F5F7", emoji: "⏱" };
  return (
    <div style={{
      maxWidth: 460, margin: "0 auto",
      background: palette.bg,
      border: `1.5px solid ${palette.border}`,
      borderLeft: `4px solid ${palette.border}`,
      borderRadius: 14, padding: "16px 18px",
      fontFamily: ff,
      animation: `icFadeIn ${FADE_MS}ms ease-out`,
    }}>
      <div style={{ fontSize: 26, marginBottom: 6 }}>{palette.emoji}</div>
      <div style={{ fontSize: 15, color: "#222", lineHeight: 1.55, fontWeight: 500 }}>
        {message}
      </div>
    </div>
  );
}

// Normalise reveal items to a common shape regardless of which variant authored them.
function normaliseReveal(data) {
  if (Array.isArray(data?.reveal)) {
    return data.reveal.map(r => ({ en: r?.en ?? r?.text ?? "", hi: r?.hi ?? r?.textHi ?? "" }));
  }
  if (Array.isArray(data?.revealSequence)) {
    return data.revealSequence.map(r => ({ en: r?.text ?? r?.en ?? "", hi: r?.textHi ?? r?.hi ?? "" }));
  }
  return [];
}

// Resolve outcome message regardless of variant.
function getOutcomeMessage(outcome, data, lang) {
  if (outcome === "correct") {
    return pickLang(data?.celebrationOnCorrect, data?.celebrationOnCorrectHi, lang);
  }
  if (outcome === "wrong") {
    return pickLang(data?.encourageOnWrong, data?.encourageOnWrongHi, lang);
  }
  if (outcome === "timeout") {
    if (data?.timeoutMessage && typeof data.timeoutMessage === "object") {
      return pickLang(data.timeoutMessage.en, data.timeoutMessage.hi, lang);
    }
    return pickLang(data?.encourageOnTimeout, data?.encourageOnTimeoutHi, lang);
  }
  return "";
}

export function InteractiveChallengeSlide({ data, language, onNext, onPrev }) {
  const lang = language === "hi" ? "hi" : "en";
  const ff = getFontFamily(lang);

  // Schema detection
  const isPrimeBuild = data?.type === "buildPrimeFactorisation"
    || data?.inputType === "tilesToTarget"
    || Array.isArray(data?.primeTiles);
  const isMatchPairs = !isPrimeBuild && (data?.inputType === "matchPairs"
    || (data?.correctMatches && typeof data.correctMatches === "object"));
  const isTwoNumbers = !isPrimeBuild && !isMatchPairs && (data?.inputType === "twoNumbers"
    || (Array.isArray(data?.answer) && data.answer.length === 2));
  const orderSensitive = !!data?.orderSensitive;

  // Match-pairs schema bits
  const leftColumn = Array.isArray(data?.leftColumn) ? data.leftColumn : [];
  const rightColumn = Array.isArray(data?.rightColumn) ? data.rightColumn : [];
  const correctMatches = (data?.correctMatches && typeof data.correctMatches === "object") ? data.correctMatches : {};
  const diagramSrc = data?.diagram?.src || null;
  const diagramCaption = pickLang(data?.diagram?.caption, data?.diagram?.captionHi, lang);

  // buildPrimeFactorisation schema bits
  const primeTiles = Array.isArray(data?.primeTiles) ? data.primeTiles.map(Number) : [];
  const target = Number(data?.target) || null;

  const title = data?.title || "Quick — try this";
  const promptText = pickLang(
    data?.prompt ?? data?.challenge,
    data?.promptHi ?? data?.challengeHi,
    lang
  );
  const placeholderEn = data?.placeholderText || (isTwoNumbers ? "Number" : "Your answer");
  const placeholder = pickLang(placeholderEn, data?.placeholderTextHi, lang);
  // For the two-number variant, label each input distinctly so the student
  // doesn't have to guess which box is which.
  const placeholder1 = isTwoNumbers ? `${placeholder} 1` : placeholder;
  const placeholder2 = isTwoNumbers ? `${placeholder} 2` : placeholder;

  const startSeconds = Number.isFinite(data?.timerSeconds) ? data.timerSeconds : 30;
  const correctAnswer = data?.correctAnswer;
  const answerArr = Array.isArray(data?.answer) ? data.answer.map(Number) : null;
  const revealItems = normaliseReveal(data);

  // Tap-to-advance reveal for tap-driven variants (twoNumbers, matchPairs, primeBuild).
  const manualReveal = isTwoNumbers || isMatchPairs || isPrimeBuild;

  const [phase, setPhase] = useState("challenge");
  const [secondsLeft, setSecondsLeft] = useState(startSeconds);
  const [userInput, setUserInput] = useState("");
  const [userInput2, setUserInput2] = useState("");
  const [outcome, setOutcome] = useState(null);
  const [revealedStep, setRevealedStep] = useState(0);
  const [wrongShake, setWrongShake] = useState(false);
  // Match-pairs state — { [leftId]: rightId }, plus a "pending left" id for the
  // tap-left-then-tap-right pairing flow.
  const [matchPairs, setMatchPairs] = useState({});
  const [pendingLeft, setPendingLeft] = useState(null);
  // buildPrimeFactorisation state — sequence of tapped primes in tap order.
  const [tappedPrimes, setTappedPrimes] = useState([]);
  const inputRef = useRef(null);

  // Running product for the prime-build variant. Reduces to 1 on empty.
  const runningProduct = isPrimeBuild
    ? tappedPrimes.reduce((acc, p) => acc * p, 1)
    : null;

  // Auto-focus first input on the challenge phase
  useEffect(() => {
    if (phase === "challenge" && inputRef.current) {
      try { inputRef.current.focus(); } catch (_e) { /* no-op */ }
    }
  }, [phase]);

  const inputsFilled = isPrimeBuild
    ? (tappedPrimes.length > 0)
    : isMatchPairs
      ? (Object.keys(matchPairs).length === leftColumn.length && leftColumn.length > 0)
      : isTwoNumbers
        ? (userInput.trim() !== "" && userInput2.trim() !== "")
        : (userInput.trim() !== "");

  const checkAnswer = () => {
    if (isPrimeBuild) return runningProduct === target;
    if (isMatchPairs) {
      if (leftColumn.length === 0) return false;
      return leftColumn.every(item => matchPairs[item.id] === correctMatches[item.id]);
    }
    if (isTwoNumbers) {
      if (!answerArr) return false;
      const a = Number(userInput.trim());
      const b = Number(userInput2.trim());
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      if (orderSensitive) return a === answerArr[0] && b === answerArr[1];
      return (a === answerArr[0] && b === answerArr[1])
          || (a === answerArr[1] && b === answerArr[0]);
    }
    if (correctAnswer === undefined) return false;
    return Number(userInput) === Number(correctAnswer);
  };

  // Prime-build tap handlers
  const onPrimeTap = (p) => { if (phase === "challenge") setTappedPrimes(prev => [...prev, p]); };
  const onPrimeUndo = () => { if (phase === "challenge") setTappedPrimes(prev => prev.slice(0, -1)); };
  const onPrimeReset = () => { if (phase === "challenge") setTappedPrimes([]); };

  // Match-pairs tap handlers
  const onLeftTap = (id) => {
    if (phase !== "challenge") return;
    if (matchPairs[id]) {
      // Already paired → unpair
      setMatchPairs(p => { const np = { ...p }; delete np[id]; return np; });
      setPendingLeft(null);
      return;
    }
    setPendingLeft(prev => (prev === id ? null : id));
  };
  const onRightTap = (rightId) => {
    if (phase !== "challenge") return;
    if (!pendingLeft) {
      // If this right is paired, unpair its left side
      const owner = Object.keys(matchPairs).find(k => matchPairs[k] === rightId);
      if (owner) {
        setMatchPairs(p => { const np = { ...p }; delete np[owner]; return np; });
      }
      return;
    }
    setMatchPairs(p => {
      const np = { ...p };
      // Remove any existing left that maps to this right (one-to-one)
      Object.keys(np).forEach(k => { if (np[k] === rightId) delete np[k]; });
      np[pendingLeft] = rightId;
      return np;
    });
    setPendingLeft(null);
  };

  // Auto-detect correct the moment running product hits target.
  useEffect(() => {
    if (isPrimeBuild && phase === "challenge" && target && runningProduct === target) {
      setOutcome("correct");
      setPhase("submitted");
    }
  }, [isPrimeBuild, phase, runningProduct, target]);

  // Countdown
  useEffect(() => {
    if (phase !== "challenge") return undefined;
    if (secondsLeft <= 0) {
      // Timer expired. If user has filled inputs and they happen to be right,
      // count that as correct; otherwise treat as timeout (unfilled or wrong).
      // (For tap-driven variants — twoNumbers, matchPairs — we deliberately
      // don't surface a "wrong" outcome here; the spec only differentiates
      // correct vs timeout.)
      if (inputsFilled && checkAnswer()) {
        setOutcome("correct");
      } else if (!isTwoNumbers && !isMatchPairs && !isPrimeBuild && inputsFilled) {
        setOutcome("wrong");
      } else {
        setOutcome("timeout");
      }
      setPhase("submitted");
      return undefined;
    }
    const tid = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(tid);
  }, [phase, secondsLeft, userInput, userInput2, isTwoNumbers]); // eslint-disable-line react-hooks/exhaustive-deps

  // submitted → revealing after a short hold
  useEffect(() => {
    if (phase !== "submitted") return undefined;
    const hasMessage = !!getOutcomeMessage(outcome, data, lang);
    const delay = hasMessage ? SUBMIT_HOLD_MS : 200;
    const tid = setTimeout(() => {
      setRevealedStep(0);
      setPhase("revealing");
    }, delay);
    return () => clearTimeout(tid);
  }, [phase, outcome, data, lang]);

  // Reveal: auto-step OR wait for tap depending on manualReveal
  useEffect(() => {
    if (phase !== "revealing") return undefined;
    if (revealItems.length === 0) { setPhase("done"); return undefined; }
    if (manualReveal) return undefined;
    if (revealedStep >= revealItems.length - 1) {
      const tid = setTimeout(() => setPhase("done"), 800);
      return () => clearTimeout(tid);
    }
    const tid = setTimeout(() => setRevealedStep(s => s + 1), STEP_DELAY_MS);
    return () => clearTimeout(tid);
  }, [phase, revealedStep, revealItems.length, manualReveal]);

  const submit = () => {
    if (phase !== "challenge" || !inputsFilled) return;
    if (checkAnswer()) {
      setOutcome("correct");
      setPhase("submitted");
    } else if (isTwoNumbers || isMatchPairs || isPrimeBuild) {
      // Tap-driven variants: keep the student on the challenge until they
      // get it (or time runs out). Brief shake to flag the wrong attempt.
      setWrongShake(true);
      setTimeout(() => setWrongShake(false), 500);
    } else {
      // Single-number variant: surface a "wrong" outcome and continue.
      setOutcome("wrong");
      setPhase("submitted");
    }
  };

  const onKeyDown = (e) => { if (e.key === "Enter") submit(); };

  // Skip — bail out of the timed challenge and jump straight to the reveal.
  // Treated like a soft timeout: outcome carries the timeout-style messaging
  // when the schema provides one, otherwise the reveal renders on its own.
  const skip = () => {
    if (phase !== "challenge") return;
    setOutcome("timeout");
    setPhase("submitted");
  };

  const handleManualNext = () => {
    if (phase === "revealing") {
      if (revealedStep >= revealItems.length - 1) {
        setPhase("done");
      } else {
        setRevealedStep(s => s + 1);
      }
    } else if (phase === "done") {
      onNext?.();
    }
  };

  const submitDisabled = !inputsFilled;
  const timerWarn = secondsLeft <= 3 && phase === "challenge";
  const outcomeMessage = getOutcomeMessage(outcome, data, lang);

  // Footer Next behaviour: in manualReveal mode the Next button drives reveal
  // progression as well as the final advance; in auto mode it only enables at
  // phase === "done".
  const nextEnabled = manualReveal
    ? (phase === "revealing" || phase === "done")
    : (phase === "done");
  const nextLabel = manualReveal && phase === "revealing"
    ? (revealedStep >= revealItems.length - 1 ? "Next ›" : "Continue ›")
    : "Next ›";
  const nextHandler = manualReveal ? handleManualNext : (() => onNext?.());

  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      background: "#FFFFFF", fontFamily: ff, width: "100%", minHeight: 0,
    }}>
      <style>{`
        @keyframes icFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes icPulse  { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }
        @keyframes icShake  { 0%,100% { transform: translateX(0); } 20% { transform: translateX(-6px); } 40% { transform: translateX(6px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(4px); } }
        .ic-no-spin::-webkit-outer-spin-button, .ic-no-spin::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .ic-no-spin { -moz-appearance: textfield; appearance: textfield; }
      `}</style>

      {/* Header */}
      <div style={{
        padding: "14px 20px",
        borderBottom: "1px solid #EEEEEE",
        background: "#FFFFFF",
        textAlign: "center",
        flexShrink: 0,
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#1A2744", fontFamily: ff }}>
          {title}
        </div>
      </div>

      {/* Body — scroll container */}
      <div style={{
        flex: 1, overflowY: "auto", padding: "20px 16px 24px",
        WebkitOverflowScrolling: "touch",
        display: "flex", flexDirection: "column", alignItems: "stretch",
      }}>
        {/* Challenge prompt */}
        {phase === "challenge" && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18, animation: `icFadeIn ${FADE_MS}ms ease-out` }}>
            <div style={{
              fontSize: 16, color: "#3D4A5C", lineHeight: 1.55, textAlign: "center",
              maxWidth: 420, fontFamily: ff, fontWeight: 500,
            }}>
              {promptText}
            </div>

            {/* Timer */}
            <div style={{
              padding: "12px 22px", borderRadius: 12,
              background: timerWarn ? "#FFE8E8" : "#E8F4FA",
              border: `1.5px solid ${timerWarn ? "#D94A4A" : "#6AAAC8"}`,
              fontFamily: "Menlo,Consolas,'SF Mono',monospace",
              fontSize: 32, fontWeight: 700,
              color: timerWarn ? "#D94A4A" : "#185FA5",
              minWidth: 130, textAlign: "center",
              transition: "background 0.2s, border-color 0.2s, color 0.2s",
            }}>
              ⏱ {formatTimer(secondsLeft)}
            </div>

            {/* Diagram (matchPairs only) */}
            {isMatchPairs && diagramSrc && (
              <div style={{
                width: "100%", maxWidth: 460,
                background: "#FFFFFF", border: "1px solid #EEEEEE",
                borderRadius: 8, padding: 8, textAlign: "center",
              }}>
                <img
                  src={diagramSrc}
                  alt={diagramCaption || "Challenge diagram"}
                  style={{ maxWidth: "100%", maxHeight: "30vh", objectFit: "contain" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                {diagramCaption && (
                  <div style={{ fontSize: 12, color: "#888", marginTop: 6, fontStyle: "italic" }}>
                    {diagramCaption}
                  </div>
                )}
              </div>
            )}

            {/* Inputs */}
            {isPrimeBuild ? (
              <div style={{
                width: "100%", maxWidth: 460,
                display: "flex", flexDirection: "column", gap: 14,
                animation: wrongShake ? "icShake 0.5s ease" : "none",
              }}>
                {/* Running product / target display */}
                <div style={{
                  background: "#FFFFFF",
                  border: "1.5px solid #CFD6E0",
                  borderRadius: 12,
                  padding: "14px 16px",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: 11, color: "#5A7A8A", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
                    {lang === "hi" ? "Running product" : "Running product"}
                  </div>
                  <div style={{
                    fontSize: 22, fontWeight: 700,
                    fontFamily: "Menlo,Consolas,'SF Mono',monospace",
                    color: runningProduct === target ? "#1A7A52" : "#1A2744",
                    lineHeight: 1.3, wordBreak: "break-word",
                  }}>
                    {tappedPrimes.length === 0
                      ? "—"
                      : `${tappedPrimes.join(" × ")} = ${runningProduct}`}
                  </div>
                  <div style={{ fontSize: 12, color: "#5A7A8A", marginTop: 6 }}>
                    {lang === "hi" ? "Target: " : "Target: "}
                    <span style={{ fontWeight: 700, color: "#1A2744" }}>{target ?? "—"}</span>
                  </div>
                </div>

                {/* Prime tile grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(64px, 1fr))", gap: 8 }}>
                  {primeTiles.map((p, i) => (
                    <button
                      key={`${p}-${i}`}
                      type="button"
                      onClick={() => onPrimeTap(p)}
                      style={{
                        minHeight: 56, padding: "10px 8px",
                        background: "#F8FBFD",
                        border: "1.5px solid #6AAAC8",
                        borderRadius: 12,
                        fontSize: 20, fontWeight: 700, fontFamily: ff,
                        color: "#185FA5",
                        cursor: "pointer",
                      }}>
                      {p}
                    </button>
                  ))}
                </div>

                {/* Undo / Reset */}
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  <button
                    type="button"
                    onClick={onPrimeUndo}
                    disabled={tappedPrimes.length === 0}
                    style={{
                      minHeight: 36, padding: "8px 14px",
                      background: "#FFFFFF", border: "1px solid #CFD6E0", borderRadius: 10,
                      fontSize: 13, fontWeight: 600, fontFamily: ff, color: "#3D4A5C",
                      cursor: tappedPrimes.length === 0 ? "default" : "pointer",
                      opacity: tappedPrimes.length === 0 ? 0.5 : 1,
                    }}>
                    ← Undo
                  </button>
                  <button
                    type="button"
                    onClick={onPrimeReset}
                    disabled={tappedPrimes.length === 0}
                    style={{
                      minHeight: 36, padding: "8px 14px",
                      background: "#FFFFFF", border: "1px solid #CFD6E0", borderRadius: 10,
                      fontSize: 13, fontWeight: 600, fontFamily: ff, color: "#3D4A5C",
                      cursor: tappedPrimes.length === 0 ? "default" : "pointer",
                      opacity: tappedPrimes.length === 0 ? 0.5 : 1,
                    }}>
                    ↺ Reset
                  </button>
                </div>
              </div>
            ) : isMatchPairs ? (
              <div style={{
                display: "flex", gap: 10, width: "100%", maxWidth: 460,
                animation: wrongShake ? "icShake 0.5s ease" : "none",
              }}>
                {/* Left column */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  {leftColumn.map(item => {
                    const pairedRight = matchPairs[item.id];
                    const isPending = pendingLeft === item.id;
                    const isPaired = !!pairedRight;
                    const pairLabel = isPaired ? rightColumn.find(r => r.id === pairedRight)?.display : null;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onLeftTap(item.id)}
                        style={{
                          minHeight: 44, padding: "10px 12px",
                          background: isPaired ? "#F0FAF4" : (isPending ? "#E8F4FA" : "#FFFFFF"),
                          border: `1.5px solid ${isPaired ? "#1A7A52" : (isPending ? "#6AAAC8" : "#CFD6E0")}`,
                          borderRadius: 10,
                          fontSize: 14, fontFamily: ff, fontWeight: 600,
                          color: isPaired ? "#1A7A52" : "#1A2744",
                          cursor: "pointer", textAlign: "center",
                          transition: "background 0.15s, border-color 0.15s",
                        }}>
                        {item.display}
                        {pairLabel && (
                          <div style={{ fontSize: 11, color: "#1A7A52", fontWeight: 500, marginTop: 2 }}>
                            → {pairLabel}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Right column */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  {rightColumn.map(item => {
                    const pairedLeftId = Object.keys(matchPairs).find(k => matchPairs[k] === item.id);
                    const isPaired = !!pairedLeftId;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onRightTap(item.id)}
                        style={{
                          minHeight: 44, padding: "10px 12px",
                          background: isPaired ? "#F0FAF4" : "#FFFFFF",
                          border: `1.5px solid ${isPaired ? "#1A7A52" : "#CFD6E0"}`,
                          borderRadius: 10,
                          fontSize: 14, fontFamily: ff, fontWeight: 600,
                          color: isPaired ? "#1A7A52" : "#1A2744",
                          cursor: "pointer", textAlign: "center",
                          transition: "background 0.15s, border-color 0.15s",
                          opacity: !pendingLeft && !isPaired ? 0.6 : 1,
                        }}>
                        {item.display}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : isTwoNumbers ? (
              <div style={{
                display: "flex", gap: 10, width: "100%", maxWidth: 320,
                animation: wrongShake ? "icShake 0.5s ease" : "none",
              }}>
                <input
                  ref={inputRef}
                  type="number"
                  inputMode="numeric"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={placeholder1}
                  aria-label={placeholder1}
                  className="ic-no-spin"
                  style={{
                    flex: 1, minWidth: 0, padding: "12px 14px",
                    fontSize: 16, fontFamily: ff,
                    border: `1.5px solid ${wrongShake ? "#D94A4A" : "#CFD6E0"}`,
                    borderRadius: 10, outline: "none", textAlign: "center",
                    transition: "border-color 0.2s",
                    background: "#FFFFFF",
                  }}
                />
                <input
                  type="number"
                  inputMode="numeric"
                  value={userInput2}
                  onChange={(e) => setUserInput2(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={placeholder2}
                  aria-label={placeholder2}
                  className="ic-no-spin"
                  style={{
                    flex: 1, minWidth: 0, padding: "12px 14px",
                    fontSize: 16, fontFamily: ff,
                    border: `1.5px solid ${wrongShake ? "#D94A4A" : "#CFD6E0"}`,
                    borderRadius: 10, outline: "none", textAlign: "center",
                    transition: "border-color 0.2s",
                    background: "#FFFFFF",
                  }}
                />
              </div>
            ) : (
              <input
                ref={inputRef}
                type="number"
                inputMode="numeric"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                style={{
                  width: "100%", maxWidth: 320,
                  padding: "12px 14px",
                  fontSize: 16, fontFamily: ff,
                  border: "1.5px solid #CFD6E0",
                  borderRadius: 10, outline: "none", textAlign: "center",
                  animation: wrongShake ? "icShake 0.5s ease" : "none",
                }}
              />
            )}

            {!isPrimeBuild && (
              <button
                type="button"
                onClick={submit}
                disabled={submitDisabled}
                style={{
                  minHeight: 44, padding: "12px 28px",
                  background: submitDisabled
                    ? "#E5E7EB"
                    : "linear-gradient(135deg, #1A7A52, #228B5A)",
                  color: submitDisabled ? "#9AA0A6" : "#FFFFFF",
                  border: "none", borderRadius: 12,
                  fontSize: 15, fontWeight: 600, fontFamily: ff,
                  cursor: submitDisabled ? "default" : "pointer",
                  boxShadow: submitDisabled ? "none" : "0 4px 12px rgba(26,122,82,0.25)",
                  letterSpacing: "-0.01em",
                }}>
                Submit
              </button>
            )}

            <button
              type="button"
              onClick={skip}
              style={{
                marginTop: -4,
                background: "transparent",
                border: "none",
                color: "#6AAAC8",
                fontSize: 13, fontWeight: 500, fontFamily: ff,
                cursor: "pointer", padding: "8px 12px",
                textDecoration: "underline",
              }}>
              {lang === "hi" ? "Skip karein →" : "Skip →"}
            </button>
          </div>
        )}

        {/* Submitted (transient) */}
        {phase === "submitted" && outcome && outcomeMessage && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 12, marginTop: 24 }}>
            <OutcomeCard outcome={outcome} message={outcomeMessage} ff={ff} />
          </div>
        )}

        {/* Revealing / done */}
        {(phase === "revealing" || phase === "done") && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {outcome && outcomeMessage && (
              <OutcomeCard outcome={outcome} message={outcomeMessage} ff={ff} />
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
              {revealItems.map((step, i) => {
                if (i > revealedStep && phase !== "done") return null;
                const isFinal = i === revealItems.length - 1;
                const stepText = pickLang(step?.en, step?.hi, lang);
                return (
                  <div
                    key={i}
                    style={{
                      maxWidth: 460, margin: "0 auto", width: "100%",
                      background: isFinal ? "#FFD6A0" : "#F8FBFD",
                      border: `1px solid ${isFinal ? "#E8B870" : "#E1E8F0"}`,
                      borderRadius: 12,
                      padding: isFinal ? "14px 18px" : "12px 14px",
                      fontFamily: ff,
                      fontSize: isFinal ? 16 : 15,
                      fontWeight: isFinal ? 700 : 500,
                      color: isFinal ? "#3D2E15" : "#1A2744",
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                      animation: `icFadeIn ${FADE_MS}ms ease-out`,
                    }}>
                    {stepText}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div style={{
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        borderTop: "1px solid #EEEEEE",
        background: "#FFFFFF",
        flexShrink: 0,
        boxSizing: "border-box",
      }}>
        <button
          onClick={onPrev}
          style={{
            minHeight: 44, minWidth: 44, padding: "10px 14px",
            background: "transparent", border: "none", borderRadius: 10,
            fontSize: 14, fontWeight: 600, fontFamily: ff, color: "#6AAAC8",
            cursor: "pointer",
          }}>
          ‹ Previous
        </button>
        <button
          onClick={() => { if (nextEnabled) nextHandler(); }}
          disabled={!nextEnabled}
          style={{
            minHeight: 44, minWidth: 44, padding: "10px 20px",
            background: nextEnabled ? "#1A7A52" : "#E5E7EB",
            color: nextEnabled ? "#FFFFFF" : "#9AA0A6",
            border: "none", borderRadius: 12,
            fontSize: 14, fontWeight: 600, fontFamily: ff,
            cursor: nextEnabled ? "pointer" : "default",
            boxShadow: nextEnabled ? "0 4px 14px rgba(26,122,82,0.3)" : "none",
            animation: phase === "done" ? "icPulse 1.4s ease-in-out infinite" : "none",
          }}>
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

export default InteractiveChallengeSlide;
